import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  IPS_ADMIN_PASSWORD,
  createDefaultPlanRows,
  getPlanTemplateSafe,
  savePlanTemplateSafe,
  getDayNotesSafe,
  saveDayNotesSafe,
  getContentsByDate,
  saveContent,
  markContentShared,
  getManualSharesByDate,
  markPlanRowSharedManually,
  manualShareDocId,
  inferContentTypeFromRowLabel,
  isGonderiContentType,
  getUploadablePlanRows,
  mapPlanRowsToContents,
  buildShareReport
} from '../utils/ipsService';
import {
  formatDateInput,
  formatDateFullLabelLines,
  formatDateWithWeekday
} from '../utils/ipsDateUtils';
import { copyTextSafe } from '../utils/ipsClipboard';
import {
  buildIpsMediaDownloadUrl,
  triggerIpsMediaDownload,
  getIpsContentFiles,
  isCarouselContent,
  isVideoMediaFile,
  openIpsMediaInNewTab,
  downloadAllIpsFiles
} from '../utils/ipsDownload';
import './ips.css';

const ADMIN_SESSION_KEY = 'ips_admin_session_v1';
const IPS_VIEWPORT =
  'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
const DEFAULT_VIEWPORT = 'width=device-width, initial-scale=1';

function useIpsNoZoomViewport() {
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const previous = meta.getAttribute('content') || DEFAULT_VIEWPORT;
    meta.setAttribute('content', IPS_VIEWPORT);
    return () => {
      meta.setAttribute('content', previous);
    };
  }, []);
}

async function copyTextToClipboard(text, label) {
  if (!text?.trim()) {
    toast.info(`${label} boş.`);
    return;
  }
  const ok = await copyTextSafe(text);
  if (ok) {
    toast.success('Kopyalandı.');
  } else {
    toast.error('Kopyalanamadı. Metni basılı tutup seçmeyi deneyin.');
  }
}

function withPlanRowLabel(content, planRowLabel) {
  if (!content) return null;
  return {
    ...content,
    planRowLabel: content.planRowLabel || planRowLabel || content.title || content.type || ''
  };
}

function IpsDownloadButton({ content, fileIndex, totalFiles, variant = 'mini' }) {
  const [status, setStatus] = useState('idle');

  const idleLabel =
    totalFiles > 1
      ? `Dosya ${fileIndex + 1}'i indir`
      : variant === 'full'
        ? 'İçeriği İndir'
        : 'İndir';

  const labelMap = {
    idle: idleLabel,
    downloading: 'İndiriliyor...',
    done: 'İndirildi',
    error: 'İndirilemedi'
  };

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    if (status === 'downloading' || !content?.id) return;

    setStatus('downloading');
    const ok = triggerIpsMediaDownload(content.id, fileIndex);
    if (ok) {
      setStatus('done');
      window.setTimeout(() => setStatus('idle'), 2000);
      return;
    }

    setStatus('error');
    toast.error('İndirme başlatılamadı.');
    window.setTimeout(() => setStatus('idle'), 2500);
  };

  const className =
    variant === 'full'
      ? `ips-btn ips-btn-ghost ips-download-btn ${status}`
      : `ips-mini-btn ips-download-btn ${status}`;

  return (
    <button
      type="button"
      className={className}
      disabled={status === 'downloading' || !content?.id}
      onClick={handleClick}
    >
      {labelMap[status]}
    </button>
  );
}

function IpsDownloadAllButton({ content, files, variant = 'mini' }) {
  const [status, setStatus] = useState('idle');

  const labelMap = {
    idle: 'Tüm Görselleri İndir',
    downloading: 'İndiriliyor...',
    done: 'İndirildi',
    error: 'İndirilemedi'
  };

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    if (status === 'downloading' || !files.length) return;
    setStatus('downloading');
    const result = await downloadAllIpsFiles(content, files);
    if (result.success) {
      setStatus('done');
      window.setTimeout(() => setStatus('idle'), 2000);
      return;
    }
    if (result.successCount > 0) {
      setStatus('error');
      toast.warn(`${result.successCount}/${result.total} dosya indirildi.`);
      window.setTimeout(() => setStatus('idle'), 3000);
      return;
    }
    setStatus('error');
    window.setTimeout(() => setStatus('idle'), 2500);
  };

  const className =
    variant === 'full'
      ? `ips-btn ips-btn-gold ips-download-btn ${status}`
      : `ips-mini-btn ips-download-all-btn ips-download-btn ${status}`;

  return (
    <button
      type="button"
      className={className}
      disabled={status === 'downloading'}
      onClick={handleClick}
    >
      {labelMap[status]}
    </button>
  );
}

function IpsContentDownloadButtons({ content, planRowLabel, variant = 'mini' }) {
  const enriched = withPlanRowLabel(content, planRowLabel);
  const files = getIpsContentFiles(enriched);
  const isMulti = files.length > 1;

  if (!files.length) {
    return null;
  }

  if (!isMulti) {
    return (
      <IpsDownloadButton
        content={enriched}
        file={files[0]}
        fileIndex={0}
        totalFiles={1}
        variant={variant}
      />
    );
  }

  return (
    <div className={`ips-download-group ${variant}`}>
      <IpsDownloadAllButton content={enriched} files={files} variant={variant} />
      {files.map((file, index) => (
        <IpsDownloadButton
          key={`${enriched?.id || planRowLabel || 'file'}-${index}`}
          content={enriched}
          file={file}
          fileIndex={index}
          totalFiles={files.length}
          variant={variant}
        />
      ))}
    </div>
  );
}

function IpsVideoModalActions({ contentId, fileUrl, fileIndex = 0 }) {
  if (!contentId || !fileUrl) return null;

  const handleOpen = (e) => {
    e?.stopPropagation?.();
    if (!openIpsMediaInNewTab(fileUrl)) {
      toast.error('Video açılamadı.');
    }
  };

  const handleDownload = (e) => {
    e?.stopPropagation?.();
    if (triggerIpsMediaDownload(contentId, fileIndex)) {
      return;
    }
    toast.error('İndirme bağlantısı oluşturulamadı.');
  };

  return (
    <div className="ips-video-modal-actions" onClick={(e) => e.stopPropagation()} role="presentation">
      <button type="button" className="ips-btn ips-btn-ghost ips-video-modal-btn" onClick={handleOpen}>
        ↗ Videoyu Aç
      </button>
      <button type="button" className="ips-btn ips-btn-gold ips-video-modal-btn" onClick={handleDownload}>
        📥 Videoyu İndir
      </button>
    </div>
  );
}

function IpsFieldHeader({ label, text }) {
  return (
    <div className="ips-label-row">
      <span className="ips-label">{label}</span>
      <button
        type="button"
        className="ips-copy-btn"
        onClick={() => copyTextToClipboard(text, label)}
      >
        Copy
      </button>
    </div>
  );
}

function isMediaFile(file) {
  return file && (file.type.startsWith('image/') || file.type.startsWith('video/'));
}

function IpsUploadZone({ file, onFileChange, inputRef }) {
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);

  const applyFile = (nextFile) => {
    if (!nextFile) {
      onFileChange(null);
      return;
    }
    if (!isMediaFile(nextFile)) {
      toast.error('Sadece görsel veya video yükleyebilirsiniz.');
      return;
    }
    onFileChange(nextFile);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    applyFile(e.dataTransfer.files?.[0] || null);
  };

  return (
    <div
      className={`ips-upload-zone ${dragActive ? 'drag-active' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Görsel veya video yükle"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        onChange={(e) => applyFile(e.target.files?.[0] || null)}
        tabIndex={-1}
      />
      <strong style={{ color: '#d4af62', fontSize: 15 }}>
        {file ? file.name : 'Dosya Seç'}
      </strong>
      <span>
        {dragActive ? 'Dosyayı buraya bırakın' : 'Dokunarak veya sürükleyerek yükle'}
      </span>
    </div>
  );
}

function IpsMediaCarousel({ files, alt = 'IPS içerik', compact = false }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    setIndex(0);
  }, [files]);

  if (!files?.length) return null;

  const current = files[index] || files[0];
  const isVideo =
    current.mediaType === 'video' || String(current.mediaType || '').startsWith('video/');
  const showNav = files.length > 1;

  const go = (direction) => {
    setIndex((prev) => (prev + direction + files.length) % files.length);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
    const diff = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(diff) < 40) return;
    go(diff < 0 ? 1 : -1);
  };

  return (
    <div
      className={`ips-carousel ${compact ? 'compact' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="ips-carousel-viewport">
        {showNav && (
          <button
            type="button"
            className="ips-carousel-arrow prev"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Önceki"
          >
            ◀
          </button>
        )}
        <div className="ips-carousel-slide">
          {isVideo ? (
            <video src={current.url} controls playsInline key={current.url} />
          ) : (
            <img src={current.url} alt={`${alt} ${index + 1}`} key={current.url} draggable={false} />
          )}
        </div>
        {showNav && (
          <button
            type="button"
            className="ips-carousel-arrow next"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Sonraki"
          >
            ▶
          </button>
        )}
      </div>
      {showNav && (
        <div className="ips-carousel-dots">
          {files.map((file, dotIndex) => (
            <button
              key={file.url || dotIndex}
              type="button"
              className={`ips-carousel-dot ${dotIndex === index ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setIndex(dotIndex); }}
              aria-label={`Görsel ${dotIndex + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function createCarouselFileEntry(file) {
  return {
    id: `carousel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    previewUrl: URL.createObjectURL(file)
  };
}

function IpsCarouselUpload({ files, onChange, inputRef }) {
  const [dragActive, setDragActive] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const dragDepthRef = useRef(0);

  const revokeEntry = (entry) => {
    if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
  };

  const addImageFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    if (!incoming.length) {
      toast.error('Gönderi için sadece görsel yükleyebilirsiniz.');
      return;
    }
    onChange([...files, ...incoming.map(createCarouselFileEntry)]);
  };

  const moveFile = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= files.length || fromIndex === toIndex) return;
    const next = [...files];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange(next);
  };

  const removeFile = (id) => {
    const target = files.find((entry) => entry.id === id);
    revokeEntry(target);
    onChange(files.filter((entry) => entry.id !== id));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    addImageFiles(e.dataTransfer.files);
  };

  return (
    <div className="ips-carousel-upload">
      <div
        className={`ips-upload-zone ips-carousel-drop ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            addImageFiles(e.target.files);
            e.target.value = '';
          }}
          tabIndex={-1}
        />
        <strong style={{ color: '#d4af62', fontSize: 15 }}>+ Dosya Ekle</strong>
        <span>{dragActive ? 'Görselleri buraya bırakın' : 'Çoklu seçim veya sürükle-bırak'}</span>
      </div>

      {files.length > 0 && (
        <p className="ips-carousel-upload-note">☰ Sürükle bırakarak sırala · Yukarıdan aşağıya paylaşım sırası</p>
      )}

      <div className="ips-carousel-file-list">
        {files.map((entry, index) => (
          <div
            key={entry.id}
            className={`ips-carousel-file-card ${draggingId === entry.id ? 'dragging' : ''}`}
            draggable
            onDragStart={() => setDraggingId(entry.id)}
            onDragEnd={() => setDraggingId(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const fromIndex = files.findIndex((item) => item.id === draggingId);
              if (fromIndex >= 0) moveFile(fromIndex, index);
              setDraggingId(null);
            }}
          >
            <div className="ips-carousel-file-preview">
              <img src={entry.previewUrl} alt={entry.file.name} draggable={false} />
            </div>
            <div className="ips-carousel-file-meta">
              <span className="ips-carousel-file-order">📷 Dosya {index + 1}</span>
              <span className="ips-carousel-file-name">{entry.file.name}</span>
              <div className="ips-carousel-file-actions">
                <button type="button" className="ips-carousel-action-btn" disabled={index === 0} onClick={() => moveFile(index, index - 1)}>↑</button>
                <button type="button" className="ips-carousel-action-btn" disabled={index === files.length - 1} onClick={() => moveFile(index, index + 1)}>↓</button>
                <button type="button" className="ips-carousel-action-btn danger" onClick={() => removeFile(entry.id)}>Sil</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IpsContentDateInput({ value, onChange }) {
  const inputRef = useRef(null);
  const displayLabel = formatDateWithWeekday(value);

  const openPicker = () => {
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    const input = inputRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker();
        return;
      }
    } catch {
      // ignore
    }
    input.focus({ preventScroll: true });
    input.click();
  };

  return (
    <div className="ips-content-date-wrap" onClick={openPicker}>
      <div className="ips-content-date-text">{displayLabel}</div>
      <input
        ref={inputRef}
        className="ips-content-date-input"
        type="date"
        value={value}
        onChange={onChange}
        required
        aria-label="Tarih seç"
      />
    </div>
  );
}

function IpsDateBox({ value, onChange, ariaLabel }) {
  const inputRef = useRef(null);
  const { line1, line2 } = formatDateFullLabelLines(value);

  const openPicker = () => {
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    const input = inputRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker();
        return;
      }
    } catch {
      // showPicker can throw if not in a user gesture context
    }
    input.focus({ preventScroll: true });
    input.click();
  };

  return (
    <div
      className="ips-date-picker"
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
    >
      <div className="ips-date-picker-text">
        <span>{line1}</span>
        <span>{line2}</span>
      </div>
      <input
        ref={inputRef}
        type="date"
        className="ips-date-picker-input"
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
      />
    </div>
  );
}

function TopBar({ title, onBack, onLogout, onSettings }) {
  return (
    <header className="ips-topbar">
      {onBack && (
        <button type="button" className="ips-icon-btn" onClick={onBack} aria-label="Geri">
          ←
        </button>
      )}
      <h1 className="ips-topbar-title">{title}</h1>
      <div className="ips-topbar-actions">
        {onSettings && (
          <button type="button" className="ips-icon-btn" onClick={onSettings} aria-label="Ayarlar">
            ⚙
          </button>
        )}
        {onLogout && (
          <button type="button" className="ips-icon-btn" onClick={onLogout}>
            Çıkış
          </button>
        )}
      </div>
    </header>
  );
}

function IpsContentDetailModal({ item, onClose, onMarkShared, onMarkManualShared, previewOnly = false }) {
  const videoRef = useRef(null);

  const handleClose = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!item) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [item, handleClose]);

  if (!item) return null;

  const contentForDownload = withPlanRowLabel(item.content, item.label);
  const mediaFiles = getIpsContentFiles(contentForDownload);
  const showCarousel = isCarouselContent(contentForDownload);
  const isSingleVideo = mediaFiles.length === 1 && isVideoMediaFile(mediaFiles[0]);

  return (
    <div className="ips-modal-overlay" onClick={handleClose} role="presentation">
      <button
        type="button"
        className="ips-modal-close-fixed"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="Kapat"
      >
        ✕ Kapat
      </button>
      <div className="ips-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ips-modal-head">
          <h2>{item.label || item.type}</h2>
        </div>
        {mediaFiles.length > 0 && (
          showCarousel ? (
            <IpsMediaCarousel files={mediaFiles} alt={item.label} />
          ) : isSingleVideo ? (
            <>
              <div className="ips-card-media ips-video-media">
                <video
                  ref={videoRef}
                  src={mediaFiles[0].url}
                  controls
                  playsInline
                  preload="metadata"
                />
              </div>
              <IpsVideoModalActions
                contentId={item.content?.id}
                fileUrl={mediaFiles[0].url}
                fileIndex={0}
              />
            </>
          ) : (
            <div className="ips-card-media">
              <img src={mediaFiles[0].url} alt={item.label} />
            </div>
          )
        )}
        {item.note && (
          <p className="ips-modal-note">{item.note}</p>
        )}
        {item.content ? (
          <>
            <div className="ips-field">
              <IpsFieldHeader label="Açıklama" text={item.content.description} />
              <div className="ips-readonly-box">{item.content.description || '—'}</div>
            </div>
            <div className="ips-field">
              <IpsFieldHeader label="Etiketler" text={item.content.tags} />
              <div className="ips-readonly-box">{item.content.tags || '—'}</div>
            </div>
            <div className="ips-field">
              <IpsFieldHeader label="Link" text={item.content.link} />
              <div className="ips-readonly-box">{item.content.link || '—'}</div>
            </div>
            <div className="ips-modal-actions">
              {mediaFiles.length > 0 && !isSingleVideo && (
                <IpsContentDownloadButtons
                  content={item.content}
                  planRowLabel={item.label}
                  variant="full"
                />
              )}
              {!previewOnly && !item.content.shared && onMarkShared && (
                <button
                  type="button"
                  className="ips-btn ips-btn-gold"
                  onClick={() => onMarkShared(item.content.id)}
                >
                  Paylaşıldı ✓
                </button>
              )}
              {item.content.shared && (
                <p className="ips-shared-tag done">✓ Paylaşıldı</p>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="ips-empty">Bu içerik henüz yüklenmedi.</p>
            <p className="ips-modal-note" style={{ marginTop: 0 }}>
              Kendi içeriğinizi üretip paylaştıysanız aşağıdan işaretleyin.
            </p>
            <div className="ips-modal-actions">
              {!previewOnly && !item.shared ? (
                <button
                  type="button"
                  className="ips-btn ips-btn-gold"
                  onClick={() => onMarkManualShared?.(item)}
                >
                  Paylaşıldı ✓
                </button>
              ) : (
                <p className="ips-shared-tag manual">✓ Paylaşıldı (personel)</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function IpsShareReportModal({
  open,
  onClose,
  planDate,
  setPlanDate,
  planRows,
  dayNotes,
  contents,
  manualShares,
  loading,
  onRefresh
}) {
  if (!open) return null;

  const report = buildShareReport(planRows, dayNotes, contents, manualShares);

  const statusLabel = (item) => {
    if (item.shared) {
      return item.manualShared ? 'Paylaşıldı — personel' : 'Paylaşıldı';
    }
    if (item.uploaded) return 'Yüklendi — bekliyor';
    return 'Henüz yüklenmedi';
  };

  const statusClass = (item) => {
    if (item.manualShared) return 'manual';
    if (item.content?.shared) return 'done';
    if (item.uploaded) return 'pending';
    return 'empty';
  };

  return (
    <div className="ips-share-overlay">
      <header className="ips-topbar">
        <button type="button" className="ips-icon-btn" onClick={onClose} aria-label="Kapat">
          ×
        </button>
        <h1 className="ips-topbar-title">Paylaşım Durumu</h1>
        <button type="button" className="ips-icon-btn" onClick={onRefresh} aria-label="Yenile">
          ↻
        </button>
      </header>
      <div className="ips-body">
        <div className="ips-card ips-date-card">
          <IpsDateBox
            value={planDate}
            onChange={(e) => setPlanDate(e.target.value)}
            ariaLabel="Rapor tarihi"
          />
        </div>

        <div className="ips-share-stats">
          <div className="ips-share-stat-main">
            <span className="ips-share-stat-num">{report.shared}</span>
            <span className="ips-share-stat-sep">/</span>
            <span className="ips-share-stat-den">{report.total}</span>
          </div>
          <p className="ips-share-stat-label">paylaşıldı</p>
          <p className="ips-share-stat-pct">%{report.sharedPctOfTotal} tamamlandı</p>
        </div>

        <div className="ips-share-summary">
          <div className="ips-share-summary-item">
            <strong>{report.total}</strong>
            <span>Toplam plan</span>
          </div>
          <div className="ips-share-summary-item">
            <strong>{report.uploaded}</strong>
            <span>Yüklenen</span>
          </div>
          <div className="ips-share-summary-item">
            <strong>{report.shared}</strong>
            <span>Paylaşılan</span>
          </div>
        </div>

        {report.uploaded > 0 && (
          <p className="ips-share-subnote">
            Yüklenenlerin <strong>%{report.sharedPctOfUploaded}</strong>&apos;i paylaşıldı
            ({report.shared}/{report.uploaded})
          </p>
        )}

        {loading ? (
          <div className="ips-empty">Yükleniyor...</div>
        ) : (
          <div className="ips-share-list">
            {report.items.map((item) => (
              <div key={item.id} className={`ips-share-row ${statusClass(item)}`}>
                <div className="ips-share-row-head">
                  {item.content?.shared && <span className="ips-ok-icon shared-done">✓</span>}
                  {item.manualShared && <span className="ips-ok-icon manual-shared">✓</span>}
                  <strong>{item.label}</strong>
                </div>
                {item.note && <p className="ips-share-row-note">{item.note}</p>}
                <span className={`ips-share-status ${statusClass(item)}`}>{statusLabel(item)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminSettingsOverlay({
  open,
  onClose,
  planRows,
  setPlanRows,
  onSave,
  planSaving,
  syncNote,
  onOpenShareReport
}) {
  if (!open) return null;

  const handleAddRow = () => {
    setPlanRows((prev) => [
      ...prev,
      {
        id: `row-${Date.now()}`,
        label: 'Yeni Satır'
      }
    ]);
  };

  const handleRemoveRow = (rowId) => {
    setPlanRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const handleRowLabelChange = (rowId, label) => {
    setPlanRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, label } : r)));
  };

  return (
    <div className="ips-settings-overlay">
      <header className="ips-topbar">
        <button type="button" className="ips-icon-btn" onClick={onClose} aria-label="Kapat">
          ×
        </button>
        <h1 className="ips-topbar-title">Plan Ayarları</h1>
        <div className="ips-topbar-actions">
          <button
            type="button"
            className="ips-icon-btn"
            onClick={onOpenShareReport}
            aria-label="Paylaşım durumu"
            title="Paylaşım durumu"
          >
            📊
          </button>
        </div>
      </header>
      <div className="ips-body">
        <p className="ips-settings-hint">
          Satır ekleme, silme ve ad değişiklikleri kaydedildiğinde tüm tarihler için geçerli olur.
        </p>

        {planRows.map((row) => (
          <div key={row.id} className="ips-plan-row">
            <div className="ips-plan-row-head">
              <input
                value={row.label}
                onChange={(e) => handleRowLabelChange(row.id, e.target.value)}
                aria-label="Satır adı"
              />
              <button
                type="button"
                className="ips-plan-row-del"
                onClick={() => handleRemoveRow(row.id)}
                aria-label="Satırı sil"
              >
                ×
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="ips-link-btn" onClick={handleAddRow}>
          + Satır ekle
        </button>
        {syncNote && (
          <p style={{ fontSize: 12, color: '#fbbf24', margin: '8px 0 0' }}>{syncNote}</p>
        )}

        <div className="ips-sticky-save">
          <button
            type="button"
            className="ips-btn ips-btn-gold"
            onClick={onSave}
            disabled={planSaving}
          >
            {planSaving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

function getDailyRowStatusClass(item, adminView) {
  if (item.manualShared) return 'manual-shared';
  if (item.content?.shared) return 'shared-done';
  if (item.uploaded) return adminView ? 'admin-uploaded' : 'uploaded-ready';
  return '';
}

function getDailyRowIconClass(item) {
  if (item.manualShared) return 'ips-ok-icon manual-shared';
  if (item.content?.shared) return 'ips-ok-icon shared-done';
  return '';
}

function DailyPlanList({ items, onRowClick, compact, adminView, dayNotes, onNoteChange, onNoteBlur }) {
  if (!items.length) {
    return <div className="ips-empty">Plan ayarı bulunamadı. ⚙ ile satır ekleyin.</div>;
  }

  return (
    <div className="ips-daily-list">
      {items.map((item) => {
        const statusClass = getDailyRowStatusClass(item, adminView);
        const showSharedIcon = item.manualShared || item.content?.shared;
        const iconClass = getDailyRowIconClass(item);
        const showUploadBadge = adminView && item.uploaded && !item.content?.shared && !item.manualShared;
        const showSharedBadge = adminView && item.content?.shared;
        const showManualBadge = adminView && item.manualShared;
        const contentForMedia = withPlanRowLabel(item.content, item.label);
        const mediaFiles = getIpsContentFiles(contentForMedia);
        const isSingleVideo = mediaFiles.length === 1 && isVideoMediaFile(mediaFiles[0]);
        const showCarousel = compact && isCarouselContent(contentForMedia) && !isSingleVideo;

        return (
        <div
          key={item.id}
          className={`ips-daily-row ${statusClass}`.trim()}
          onClick={() => onRowClick?.(item)}
          role={onRowClick ? 'button' : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onRowClick(item);
            }
          }}
        >
          <div className="ips-daily-row-main">
            {showSharedIcon && (
              <span
                className={iconClass}
                aria-label={item.manualShared ? 'Personel paylaştı' : 'Paylaşıldı'}
              >
                ✓
              </span>
            )}
            <div className="ips-daily-row-text">
              <strong>{item.label}</strong>
              {!adminView && item.note && <span>{item.note}</span>}
            </div>
            {showUploadBadge && (
              <span className="ips-row-badge uploaded" aria-label="İçerik yüklendi">
                <span className="ips-row-badge-icon" aria-hidden="true">↑</span>
                Yüklendi
              </span>
            )}
            {showSharedBadge && (
              <span className="ips-row-badge shared" aria-label="Paylaşıldı">
                Paylaşıldı
              </span>
            )}
            {showManualBadge && (
              <span className="ips-row-badge manual" aria-label="Personel paylaştı">
                Personel
              </span>
            )}
          </div>
          {adminView && (
            <input
              className="ips-input ips-daily-note-input"
              value={dayNotes?.[item.id] || ''}
              onChange={(e) => onNoteChange?.(item.id, e.target.value)}
              onBlur={() => onNoteBlur?.(item.id)}
              onClick={(e) => e.stopPropagation()}
              placeholder="İçerik notu / başlık"
              aria-label={`${item.label} notu`}
            />
          )}
          {showCarousel && (
            <div className="ips-daily-row-carousel" onClick={(e) => e.stopPropagation()} role="presentation">
              <IpsMediaCarousel files={mediaFiles} alt={item.label} compact />
            </div>
          )}
          {compact && (
            <div className="ips-daily-row-btns" onClick={(e) => e.stopPropagation()} role="presentation">
              <IpsContentDownloadButtons
                content={item.content}
                planRowLabel={item.label}
                variant="mini"
              />
              <button
                type="button"
                className="ips-mini-btn"
                disabled={!item.content?.description}
                onClick={() => copyTextToClipboard(item.content?.description, 'Açıklama')}
              >
                Açıklama
              </button>
              <button
                type="button"
                className="ips-mini-btn"
                disabled={!item.content?.tags}
                onClick={() => copyTextToClipboard(item.content?.tags, 'Etiket')}
              >
                Etiket
              </button>
              <button
                type="button"
                className="ips-mini-btn"
                disabled={!item.content?.link}
                onClick={() => copyTextToClipboard(item.content?.link, 'Link')}
              >
                Link
              </button>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

function HomeView({ onAdmin, onStaff }) {
  return (
    <div className="ips-home">
      <div className="ips-home-box">
        <div className="ips-home-brand">UKBA</div>
        <h1 className="ips-home-title">IPS</h1>
        <p className="ips-home-sub">Instagram Paylaşım Sistemi</p>
        <div className="ips-home-btns">
          <button type="button" className="ips-btn ips-btn-gold" onClick={onAdmin}>
            Yönetici Girişi
          </button>
          <button type="button" className="ips-btn ips-btn-ghost" onClick={onStaff}>
            Personel Girişi
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminLoginView({ onBack, onSuccess }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === IPS_ADMIN_PASSWORD) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, '1');
      onSuccess();
      return;
    }
    setError('Hatalı şifre.');
  };

  return (
    <div className="ips-root">
      <TopBar title="Yönetici Girişi" onBack={onBack} />
      <div className="ips-body">
        <form onSubmit={handleSubmit}>
          <div className="ips-field">
            <label className="ips-label">Şifre</label>
            <input
              className="ips-input"
              type="password"
              inputMode="numeric"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="••••"
              autoFocus
            />
          </div>
          {error && <p style={{ color: '#f87171', fontSize: 14, margin: '0 0 12px' }}>{error}</p>}
          <button type="submit" className="ips-btn ips-btn-gold">Giriş Yap</button>
        </form>
      </div>
    </div>
  );
}

function AdminPanelView({ onBack, onLogout }) {
  const [tab, setTab] = useState('plan');
  const [showSettings, setShowSettings] = useState(false);
  const [showShareReport, setShowShareReport] = useState(false);
  const [planDate, setPlanDate] = useState(formatDateInput());
  const [planRows, setPlanRows] = useState(createDefaultPlanRows);
  const [dayNotes, setDayNotes] = useState({});
  const [planSaving, setPlanSaving] = useState(false);
  const [syncNote, setSyncNote] = useState('');
  const [dailyContents, setDailyContents] = useState([]);
  const [manualShares, setManualShares] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const dayNotesSaveRef = useRef({});

  const [contentForm, setContentForm] = useState({
    date: formatDateInput(),
    planRowId: '',
    description: '',
    tags: '',
    link: ''
  });
  const [contentDayContents, setContentDayContents] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [carouselFiles, setCarouselFiles] = useState([]);
  const [contentSaving, setContentSaving] = useState(false);
  const [adminDetailItem, setAdminDetailItem] = useState(null);
  const fileInputRef = useRef(null);
  const carouselInputRef = useRef(null);

  const selectedUploadRow = planRows.find((row) => row.id === contentForm.planRowId);
  const isGonderiUpload = isGonderiContentType(
    inferContentTypeFromRowLabel(selectedUploadRow?.label || '')
  );

  const loadPlanTemplate = useCallback(async () => {
    setPlanRows(createDefaultPlanRows());
    setSyncNote('');
    try {
      const existing = await getPlanTemplateSafe();
      if (existing?.rows?.length) {
        setPlanRows(existing.rows);
      }
    } catch {
      setSyncNote('Buluta kaydedilemedi — yerel kopya kullanılıyor.');
    }
  }, []);

  const loadDayNotes = useCallback(async (date) => {
    try {
      const notes = await getDayNotesSafe(date);
      setDayNotes(notes);
      dayNotesSaveRef.current = notes;
    } catch {
      setDayNotes({});
      dayNotesSaveRef.current = {};
    }
  }, []);

  const loadDailyContents = useCallback(async (date) => {
    setDailyLoading(true);
    const [contentsResult, manualResult] = await Promise.allSettled([
      getContentsByDate(date),
      getManualSharesByDate(date)
    ]);

    const list = contentsResult.status === 'fulfilled' ? contentsResult.value : [];
    const manual = manualResult.status === 'fulfilled' ? manualResult.value : [];

    if (contentsResult.status === 'rejected') {
      console.error('IPS contents load failed:', contentsResult.reason);
    }
    if (manualResult.status === 'rejected') {
      console.warn('IPS manual shares load failed:', manualResult.reason);
    }

    setDailyContents(list);
    setManualShares(manual);
    setDailyLoading(false);
  }, []);

  const loadContentDayContents = useCallback(async (date) => {
    try {
      const list = await getContentsByDate(date);
      setContentDayContents(list);
    } catch {
      setContentDayContents([]);
    }
  }, []);

  useEffect(() => {
    loadPlanTemplate();
  }, [loadPlanTemplate]);

  useEffect(() => {
    loadDayNotes(planDate);
  }, [planDate, loadDayNotes]);

  useEffect(() => {
    if (tab === 'plan' || showShareReport) {
      loadDailyContents(planDate);
    }
  }, [tab, planDate, showShareReport, loadDailyContents]);

  useEffect(() => {
    if (tab !== 'content') return undefined;
    loadContentDayContents(contentForm.date);
    return undefined;
  }, [tab, contentForm.date, loadContentDayContents]);

  useEffect(() => {
    if (tab !== 'content') return undefined;
    setMediaFile(null);
    setCarouselFiles((prev) => {
      prev.forEach((entry) => {
        if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      });
      return [];
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (carouselInputRef.current) carouselInputRef.current.value = '';
    return undefined;
  }, [tab, contentForm.planRowId]);

  const uploadableRows = getUploadablePlanRows(planRows, contentDayContents);

  useEffect(() => {
    if (tab !== 'content') return undefined;
    setContentForm((prev) => {
      const rows = getUploadablePlanRows(planRows, contentDayContents);
      const stillValid = rows.some((row) => row.id === prev.planRowId);
      if (stillValid) return prev;
      return { ...prev, planRowId: rows[0]?.id || '' };
    });
    return undefined;
  }, [tab, contentForm.date, planRows, contentDayContents]);

  const dailyItems = mapPlanRowsToContents(planRows, dayNotes, dailyContents, manualShares);

  const handleDayNoteChange = (rowId, value) => {
    setDayNotes((prev) => {
      const next = { ...prev, [rowId]: value };
      dayNotesSaveRef.current = next;
      return next;
    });
  };

  const handleDayNoteBlur = async () => {
    try {
      await saveDayNotesSafe(planDate, dayNotesSaveRef.current);
    } catch {
      toast.warn('Not kaydedilemedi.');
    }
  };

  const handleSavePlan = async () => {
    setPlanSaving(true);
    try {
      await savePlanTemplateSafe(planRows);
      toast.success('Plan şablonu kaydedildi — tüm tarihler güncellendi.');
      setSyncNote('');
      setShowSettings(false);
    } catch {
      toast.warn('Yerel kaydedildi, buluta yazılamadı.');
      setSyncNote('Bulut senkronu başarısız — kuralları deploy edin.');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleSaveContent = async (e) => {
    e.preventDefault();
    const planRow = planRows.find((row) => row.id === contentForm.planRowId);
    if (!planRow) {
      toast.error('Yüklenecek içerik seçin.');
      return;
    }

    const gonderiUpload = isGonderiContentType(inferContentTypeFromRowLabel(planRow.label));
    const filesToUpload = gonderiUpload
      ? carouselFiles.map((entry) => entry.file)
      : mediaFile;

    if (gonderiUpload) {
      if (!carouselFiles.length) {
        toast.error('En az bir görsel seçin.');
        return;
      }
    } else if (!mediaFile) {
      toast.error('Görsel veya video seçin.');
      return;
    }

    setContentSaving(true);
    try {
      const contentType = inferContentTypeFromRowLabel(planRow.label);
      await saveContent(
        {
          date: contentForm.date,
          planRowId: planRow.id,
          planRowLabel: planRow.label,
          type: contentType || planRow.label,
          title: planRow.label,
          description: contentForm.description,
          tags: contentForm.tags,
          link: contentForm.link,
          publishTime: ''
        },
        filesToUpload
      );
      toast.success('İçerik kaydedildi.');
      const savedDate = contentForm.date;
      setContentForm((prev) => ({
        date: prev.date,
        planRowId: '',
        description: '',
        tags: '',
        link: ''
      }));
      setMediaFile(null);
      setCarouselFiles((prev) => {
        prev.forEach((entry) => {
          if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
        });
        return [];
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (carouselInputRef.current) carouselInputRef.current.value = '';
      await loadContentDayContents(savedDate);
      if (savedDate === planDate) {
        await loadDailyContents(planDate);
      }
    } catch (err) {
      console.error(err);
      toast.error('Kaydedilemedi. Firestore kurallarını kontrol edin.');
    } finally {
      setContentSaving(false);
    }
  };

  const hasUploadMedia = isGonderiUpload ? carouselFiles.length > 0 : !!mediaFile;

  return (
    <div className="ips-root">
      <TopBar
        title="Yönetici Paneli"
        onBack={onBack}
        onLogout={onLogout}
        onSettings={() => setShowSettings(true)}
      />
      <div className="ips-body">
        <div className="ips-segments">
          <button
            type="button"
            className={`ips-segment ${tab === 'plan' ? 'active' : ''}`}
            onClick={() => setTab('plan')}
          >
            Günlük Plan
          </button>
          <button
            type="button"
            className={`ips-segment ${tab === 'content' ? 'active' : ''}`}
            onClick={() => setTab('content')}
          >
            İçerik Yükle
          </button>
        </div>

        {tab === 'plan' && (
          <>
            <div className="ips-card ips-date-card">
              <IpsDateBox
                value={planDate}
                onChange={(e) => setPlanDate(e.target.value)}
                ariaLabel="Plan tarihi"
              />
            </div>

            {dailyLoading ? (
              <div className="ips-empty">Yükleniyor...</div>
            ) : (
              <DailyPlanList
                items={dailyItems}
                adminView
                dayNotes={dayNotes}
                onNoteChange={handleDayNoteChange}
                onNoteBlur={handleDayNoteBlur}
                onRowClick={setAdminDetailItem}
              />
            )}
          </>
        )}

        <IpsContentDetailModal
          item={adminDetailItem}
          onClose={() => setAdminDetailItem(null)}
          previewOnly
        />

        {tab === 'content' && (
          <form onSubmit={handleSaveContent}>
            <div className="ips-row-2">
              <div className="ips-field">
                <label className="ips-label">Tarih</label>
                <IpsContentDateInput
                  value={contentForm.date}
                  onChange={(e) => setContentForm((p) => ({ ...p, date: e.target.value }))}
                />
              </div>
              <div className="ips-field">
                <label className="ips-label">İçerik</label>
                <select
                  className="ips-select"
                  value={contentForm.planRowId}
                  onChange={(e) => setContentForm((p) => ({ ...p, planRowId: e.target.value }))}
                  required
                  disabled={!uploadableRows.length}
                >
                  {uploadableRows.length === 0 ? (
                    <option value="">Bu tarih için tüm içerikler yüklendi</option>
                  ) : (
                    uploadableRows.map((row) => (
                      <option key={row.id} value={row.id}>{row.label}</option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="ips-field">
              <label className="ips-label">
                {isGonderiUpload ? 'Görseller (Carousel)' : 'Görsel / Video'}
              </label>
              {isGonderiUpload ? (
                <IpsCarouselUpload
                  files={carouselFiles}
                  onChange={setCarouselFiles}
                  inputRef={carouselInputRef}
                />
              ) : (
                <IpsUploadZone
                  file={mediaFile}
                  onFileChange={setMediaFile}
                  inputRef={fileInputRef}
                />
              )}
            </div>

            <div className="ips-field">
              <IpsFieldHeader label="Açıklama" text={contentForm.description} />
              <textarea
                className="ips-textarea"
                value={contentForm.description}
                onChange={(e) => setContentForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Paylaşım metni"
              />
            </div>

            <div className="ips-field">
              <IpsFieldHeader label="Etiketler" text={contentForm.tags} />
              <textarea
                className="ips-textarea"
                style={{ minHeight: 64 }}
                value={contentForm.tags}
                onChange={(e) => setContentForm((p) => ({ ...p, tags: e.target.value }))}
                placeholder="#ukba #altın"
              />
            </div>

            <div className="ips-field">
              <IpsFieldHeader label="Link" text={contentForm.link} />
              <input
                className="ips-input"
                type="url"
                value={contentForm.link}
                onChange={(e) => setContentForm((p) => ({ ...p, link: e.target.value }))}
                placeholder="https://..."
              />
            </div>

            <button
              type="submit"
              className="ips-btn ips-btn-gold"
              disabled={contentSaving || !uploadableRows.length || !contentForm.planRowId || !hasUploadMedia}
            >
              {contentSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </form>
        )}
      </div>

      <AdminSettingsOverlay
        open={showSettings}
        onClose={() => setShowSettings(false)}
        planRows={planRows}
        setPlanRows={setPlanRows}
        onSave={handleSavePlan}
        planSaving={planSaving}
        syncNote={syncNote}
        onOpenShareReport={() => setShowShareReport(true)}
      />

      <IpsShareReportModal
        open={showShareReport}
        onClose={() => setShowShareReport(false)}
        planDate={planDate}
        setPlanDate={setPlanDate}
        planRows={planRows}
        dayNotes={dayNotes}
        contents={dailyContents}
        manualShares={manualShares}
        loading={dailyLoading}
        onRefresh={() => {
          loadDailyContents(planDate);
          loadDayNotes(planDate);
        }}
      />
    </div>
  );
}

function StaffPanelView({ onBack }) {
  const today = formatDateInput();
  const [selectedDate, setSelectedDate] = useState(today);
  const [planRows, setPlanRows] = useState(createDefaultPlanRows);
  const [dayNotes, setDayNotes] = useState({});
  const [contents, setContents] = useState([]);
  const [manualShares, setManualShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailItem, setDetailItem] = useState(null);

  const loadData = useCallback(async (date) => {
    setLoading(true);
    const [planResult, notesResult, contentsResult, manualResult] = await Promise.allSettled([
      getPlanTemplateSafe(),
      getDayNotesSafe(date),
      getContentsByDate(date),
      getManualSharesByDate(date)
    ]);

    const plan = planResult.status === 'fulfilled' ? planResult.value : null;
    const notes = notesResult.status === 'fulfilled' ? notesResult.value : {};
    const list = contentsResult.status === 'fulfilled' ? contentsResult.value : [];
    const manual = manualResult.status === 'fulfilled' ? manualResult.value : [];

    if (planResult.status === 'rejected') {
      console.warn('IPS plan template load failed:', planResult.reason);
    }
    if (notesResult.status === 'rejected') {
      console.warn('IPS day notes load failed:', notesResult.reason);
    }
    if (manualResult.status === 'rejected') {
      console.warn('IPS manual shares load failed:', manualResult.reason);
    }
    if (contentsResult.status === 'rejected') {
      console.error('IPS contents load failed:', contentsResult.reason);
      toast.error('Veriler yüklenemedi.', { toastId: 'ips-staff-load' });
    }

    setPlanRows(plan?.rows?.length ? plan.rows : createDefaultPlanRows());
    setDayNotes(notes || {});
    setContents(list);
    setManualShares(manual);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData(selectedDate);
  }, [selectedDate, loadData]);

  const dailyItems = mapPlanRowsToContents(planRows, dayNotes, contents, manualShares);
  const pendingCount = dailyItems.filter((i) => !i.shared).length;

  const handleMarkShared = async (contentId) => {
    try {
      await markContentShared(contentId);
      setContents((prev) => prev.map((c) => (c.id === contentId ? { ...c, shared: true } : c)));
      setDetailItem((prev) =>
        prev?.content?.id === contentId
          ? { ...prev, content: { ...prev.content, shared: true }, shared: true }
          : prev
      );
      toast.success('Tamamlandı.');
    } catch {
      toast.error('İşaretlenemedi.');
    }
  };

  const handleMarkManualShared = async (item) => {
    try {
      await markPlanRowSharedManually(selectedDate, item.id, item.label);
      const docId = manualShareDocId(selectedDate, item.id);
      setManualShares((prev) => {
        const next = prev.filter((entry) => entry.planRowId !== item.id);
        return [
          ...next,
          {
            id: docId,
            date: selectedDate,
            planRowId: item.id,
            planRowLabel: item.label,
            shared: true
          }
        ];
      });
      setDetailItem((prev) =>
        prev?.id === item.id ? { ...prev, shared: true, manualShared: true } : prev
      );
      toast.success('Paylaşım işaretlendi.');
    } catch {
      toast.error('İşaretlenemedi.');
    }
  };

  return (
    <div className="ips-root">
      <TopBar title="Personel Paneli" onBack={onBack} />
      <div className="ips-body">
        <div className="ips-card ips-date-card">
          <IpsDateBox
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            ariaLabel="Tarih seç"
          />
        </div>

        {selectedDate !== today && (
          <button
            type="button"
            className="ips-link-btn"
            style={{ marginBottom: 12 }}
            onClick={() => setSelectedDate(today)}
          >
            Bugüne dön
          </button>
        )}

        <p style={{ margin: '0 0 14px', fontSize: 14, color: 'rgba(245,230,200,0.6)' }}>
          Günlük plan
          {!loading && pendingCount > 0 && <span> · {pendingCount} bekleyen</span>}
        </p>

        {loading ? (
          <div className="ips-empty">Yükleniyor...</div>
        ) : (
          <DailyPlanList
            items={dailyItems}
            compact
            onRowClick={setDetailItem}
          />
        )}
      </div>

      <IpsContentDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onMarkShared={handleMarkShared}
        onMarkManualShared={handleMarkManualShared}
      />
    </div>
  );
}

function IpsMiniScreen() {
  const [view, setView] = useState('home');
  useIpsNoZoomViewport();

  const goHome = () => setView('home');

  const handleAdminEntry = () => {
    if (sessionStorage.getItem(ADMIN_SESSION_KEY) === '1') {
      setView('admin');
    } else {
      setView('admin-login');
    }
  };

  const handleAdminLogout = () => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setView('home');
  };

  if (view === 'admin-login') {
    return <AdminLoginView onBack={goHome} onSuccess={() => setView('admin')} />;
  }
  if (view === 'admin') {
    return <AdminPanelView onBack={goHome} onLogout={handleAdminLogout} />;
  }
  if (view === 'staff') {
    return <StaffPanelView onBack={goHome} />;
  }

  return <HomeView onAdmin={handleAdminEntry} onStaff={() => setView('staff')} />;
}

export default IpsMiniScreen;
